import Sheet from "components/sheet/Sheet";
import Portal from "elements/Portal";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IBooking, ITeeTime } from "redux/reducers/models/teetime";
import "./duplicateBookingModal.scss";
import { IFacility } from "redux/reducers/models/facility";
import moment from "moment";
import FormLayout from "components/form/FormLayout";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import { GetTeeTime } from "api/rpc/2024-04/facilityAdmin/teesheet/teeSheet";
import { convertHoles, formatDate, valueToString } from "helpers/Helpers";
import axios, { CancelToken } from "axios";
import { StatusCode } from "api/protocols";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import SelectNew from "components/select/SelectNew";
import Input from "components/form/input";
import { faArrowRight, faCheck, faCircleExclamation, faLock } from "@fortawesome/pro-regular-svg-icons";
import { Badge } from "components/badge/Badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GetValidateCopyMultipleBookings, IValidateDuplicateBookings, PutCopyBooking } from "api/rpc/2024-04/facilityAdmin/teesheet/booking";
import ReactDOM from "react-dom";
import { ButtonNew as Button} from "components/buttonNew";
import Callout from "components/callout/Callout";

interface IProps {
    open: boolean;
    booking: IBooking;
    teetime: ITeeTime;
    facility: IFacility;
    onClose: (refreshTeetimes: boolean) => void;
}

interface IState {
    startDate: Date;
    endDate: Date;
    teetimes: Array<ITeeTime>;
    selectedTeeTime: ITeeTime;
    occurances: Array<string>;
}

enum DuplicatePage {
    Step1 = 0,
    Step2 = 1,
};

const dayMap: {[key: string]: number} = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };

export default function DuplicateBookingModal(props: IProps) {
    const { open, booking, teetime, facility, onClose} = props;
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const [page, setPage] = useState<DuplicatePage>(DuplicatePage.Step1);
    const [state, setState] = useState<IState>({
        startDate: new Date(),
        endDate: new Date(),
        teetimes: undefined,
        selectedTeeTime: null,
        occurances: [],
    });

    const [validatedTimes, setValidatedTimes] = useState<IValidateDuplicateBookings>(null);

    const singleDateBooking = moment(state?.startDate).isSame(moment(state?.endDate));

    const OCCURANCES = [
        {value: "monday", label: t("Every Monday")},
        {value: "tuesday", label: t("Every Tuesday")},
        {value: "wednesday", label: t("Every Wednesday")},
        {value: "thursday", label: t("Every Thursday")},
        {value: "friday", label: t("Every Friday")},
        {value: "saturday", label: t("Every Saturday")},
        {value: "sunday", label: t("Every Sunday")},
        {value: "everyday", label: t("Everyday")},
    ];

    useEffect(() => {
        const source = axios.CancelToken.source();
        if (open && state.startDate) {
            void getTeeTimes(source.token);
        }
        return () => source.cancel();
    }, [open, state.startDate]);

    async function getTeeTimes(cancelToken: CancelToken) {
        const params = {
            facility_id: facility?.id,
            date: formatDate(state.startDate),
            quantity_remaining: booking?.quantity,
          };
        if (state.teetimes !== undefined) {
            //loader
            setState(prev => ({...prev, teetimes: undefined, selectedTeeTime: null}));
        }
        const res = await GetTeeTime(params, false);
        if (res?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error getting tee times")));
            setState(prev => ({...prev, teetimes: []}));
            return;
        }
        const filteredTeeTimes = res?.data?.filter(t => t.course_id === teetime?.course_id && t.blocked_type === "open");
        setState(prev => ({...prev, teetimes: filteredTeeTimes}));
    }

    async function duplicateBookingToSingleDate() {
        const teeTimeIds: Array<number> = [state.selectedTeeTime?.id];
        const res = await PutCopyBooking({booking_id: booking?.id, tee_time_ids: teeTimeIds}, true);
        if (res?.status !== StatusCode.OK) {
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error duplicating booking")));
            return;
        }
        dispatch(showSuccess(t("Booking copied successfully")));
        void handleClose(true);
    }

    async function duplicateMultipleBookings() {
        const teeTimeIds: Array<number> = validatedTimes?.valid_dates?.map(date => date?.tee_time_id);
        const res = await PutCopyBooking({booking_id: booking?.id, tee_time_ids: teeTimeIds}, true);
        if (res?.status !== StatusCode.OK) {
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error duplicating bookings")));
            return;
        }
        dispatch(showSuccess(t("Booking copied successfully")));
        void handleClose(true);
    }

    function onOccuranceClick(occurance: string) {
        let updatedOccurances = state?.occurances ? [...state.occurances] : [];
        if (updatedOccurances.includes(occurance)) {
            updatedOccurances = updatedOccurances?.filter(o => o !== occurance);
        } else {
            if ((updatedOccurances?.length > 0 && occurance === "everyday") || (updatedOccurances?.includes("everyday") && occurance !== "everyday")) {
                updatedOccurances = [occurance];
            } else {
                updatedOccurances.push(occurance);
            }   
        }
        setState(prev => ({...prev, occurances: updatedOccurances}));
    }


    async function validateTeeTimes() {
        const res = await GetValidateCopyMultipleBookings({
            booking_id: booking?.id,
            start_date: formatDate(state?.startDate),
            end_date: formatDate(state?.endDate),
            occurrences: state?.occurances,
        }, true);

        if (res?.status !== StatusCode.OK) {
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error validating dates")));
            return;
        }

        ReactDOM.unstable_batchedUpdates(() => {
            setValidatedTimes(res?.data);
            setPage(DuplicatePage.Step2);
        });
        
    }

    function getNumberOfBookingsToCreate() {
        let totalBookings = 0;
        if (state?.occurances.includes("everyday")) {
        const excludeCurrentDay = + moment(state.startDate).isSame(moment(), "day") ? 0 : 1;
        totalBookings = moment(state.endDate).diff(moment(state.startDate), "days") + excludeCurrentDay;
        } else {
        const start = moment(state.startDate).clone();
        const end = moment(state.endDate).clone();

        //Don't include current day
        if (start.isSame(moment(), "day")) {
            start.add(1, "day");
        }

        const selectedDays = state?.occurances?.map(day => dayMap[day.toLowerCase()]);

        const current = start;

        //Count number of days based on occurrences selection
        while (current.isSameOrBefore(end, "day")) {
                if (selectedDays.includes(current.day())) {
                totalBookings++;
                }
                current.add(1, "day");
            }
        }
        return totalBookings;
    }

    function navigateToTeeSheet(date: string) {
        window.open(`/admin/teesheet?date=${date}`, "_blank");
    }

    function handleClose(refreshTeeTimes: boolean) {
        void onClose(refreshTeeTimes);
        setState({
            startDate: new Date(),
            endDate: new Date(),
            teetimes: undefined,
            selectedTeeTime: null,
            occurances: [],
        });
    }

    return (
        <Portal
            isMounted={open}
        >
        <Sheet
            open={open}
            size="small"
            title={t("Duplicate booking")}
            onOk={singleDateBooking ? () => duplicateBookingToSingleDate() : page === DuplicatePage.Step1 ? () => validateTeeTimes() : () => duplicateMultipleBookings()}
            okText={singleDateBooking ? t("Duplicate booking") : page === DuplicatePage.Step1 ? t("Check availability") : t("Confirm & duplicate")}
            onCancel={page === DuplicatePage.Step1 ? () => handleClose(false) : () => setPage(DuplicatePage.Step1)}
            cancelText={page === DuplicatePage.Step1 ? t("Cancel") : t("Back")}
            closable
            supportText={t("Clone this booking to a single day or across a range of dates")}
            overflow
            okDisabled={singleDateBooking ? !state?.selectedTeeTime : page === DuplicatePage.Step1 ? (!state?.occurances || state?.occurances?.length === 0) : page === DuplicatePage.Step2 ? validatedTimes?.valid_dates?.length === 0 : false}
            backDropCancel={false}
        >
            {page === DuplicatePage.Step1 ? (
                <>
                <div className="ui-duplicate-booking-modal-facility-container">
                    <div className="ui-duplicate-booking-modal-facility-logo-container">
                        <img className="ui-duplicate-booking-modal-facility-logo" src={facility?.logo_source} alt={t("Facility logo")}/>
                    </div>  
                    <div>
                        <p className="ui-duplicate-booking-modal-facility-primary">{facility?.long_name}</p>
                        <p className="ui-duplicate-booking-modal-facility-secondary">{moment(`${teetime?.date}`, "YYYY-MM-DD")?.format("ll")} • {moment(`${teetime?.start_time}`, "HH:mm")?.format("LT")} • {booking?.quantity} {booking?.quantity === 1 ? t("Player") : t("Players")}</p>
                    </div>
                </div>
                <FormLayout>
                <FormLayout.Group>
                    <DatePickerInput
                        months={1}
                        position="left"
                        disablePastDays
                        startingDate={state.startDate}
                        setStartingDate={(date: Date) => setState(prev => ({...prev, startDate: date}))}
                        endingDate={state.endDate}
                        setEndingDate={(date: Date) => setState(prev => ({...prev, endDate: date}))}
                        label={t("Date(s)")}
                        />
                </FormLayout.Group>
                {!singleDateBooking && state?.occurances?.length > 0 ? (
                    <FormLayout.Group>
                        <Badge size="medium" colour="#175CD3" backgroundColour="#EFF8FF"><FontAwesomeIcon icon={faCheck} className="mr-2"/> {t("{{numDates}} bookings will be created", {numDates: getNumberOfBookingsToCreate()})}</Badge>
                    </FormLayout.Group>
                ) : null}
                <FormLayout.Group>
                    {singleDateBooking ? (
                        <SelectNew
                        id={"tee_time_select"}
                        label={t("Available times")}
                        placeholder={t("Select Time")}
                        onChange={(_: number, teetime: ITeeTime) => setState(prev => ({...prev, selectedTeeTime: teetime}))}
                        value={state.selectedTeeTime?.id}
                        isLoading={!state.teetimes}
                        helpText={!state.teetimes ? t("Loading tee times...") : t("{{numTimes}} available times for {{date}}", {numTimes: state?.teetimes?.length ?? 0, date: moment(`${teetime?.date}`, "YYYY-MM-DD")?.format("ll")})}
                        options={state?.teetimes?.map(teetime => ({
                          id: teetime.id,
                          label: moment(teetime.start_time, "HH:mm")?.format("LT"),
                          extraValue: teetime,
                        }))}
                       />
                    ) : 
                    <Input 
                        icon={faLock} 
                        labelHidden readOnly value={moment(`${teetime?.start_time}`, "HH:mm")?.format("LT")} 
                        helpText={t("We'll check availability across all {{numDates}} dates and flag any conflicts on the next step", {numDates: moment(state?.endDate).diff(state.startDate, "days") + 1})}/>
                        }
                </FormLayout.Group>
                {!singleDateBooking ? (
                    <FormLayout.Group>
                        <SelectNew
                        id={"occurance_dropdown"}
                        label={t("Occurrence")}
                        dropdownPosition="top"
                        placeholder={state?.occurances?.map(occurance => valueToString(occurance))?.join(", ") || t("Select occurrence")}
                        onChange={occurance => onOccuranceClick(occurance)}
                        multiValue={state?.occurances ?? []}
                        options={OCCURANCES?.map(occurance => ({
                          id: occurance.value,
                          label: occurance.label,
                        }))}
                       />
                    </FormLayout.Group>
                ) : null}
            </FormLayout>
            </>
            ) : page === DuplicatePage.Step2 ? (
                <div>
                    {validatedTimes?.error_count > 0 ? (
                    <>
                    <div className="ui-duplicate-booking-modal-status-container">
                            <p className="ui-duplicate-booking-modal-facility-primary">{t("Booking Status")}</p>
                            
                                 <div className="flex items-center gap-2">
                                    {validatedTimes?.valid_count > 0 ? (
                                         <div className="ui-duplicate-booking-modal-status">
                                            <div className="ui-duplicate-booking-modal-status-colour-success"></div>
                                            <p>{validatedTimes?.valid_count} {t("available")}</p>
                                        </div>
                                    ) : null}
                                
                                 <div className="ui-duplicate-booking-modal-status">
                                    <div className="ui-duplicate-booking-modal-status-colour-error"></div>
                                    <p>{validatedTimes?.error_count} {t("unavailable")}</p>
                                 </div>
                             </div>
                    </div>
                        <div className="ui-duplicate-booking-modal-status-list">
                        {validatedTimes?.errors?.map((error, index) => {
                            return (
                                <div key={index} className="ui-duplicate-booking-modal-status-list-card">
                                    <div className="flex flex-column gap-2">
                                        <p className="ui-duplicate-booking-modal-facility-primary" style={{padding: "0 8px"}}>{moment(error?.date)?.format("ll")}</p>
                                        <Badge size="small" type="error"><FontAwesomeIcon icon={faCircleExclamation} className="mr-1"/> {moment(teetime?.start_time, "HH:mm")?.format("LT")} • {error?.error ?? t("unavailable")}</Badge>
                                    </div>
                                    <Button type="text" size="small" onClick={() => navigateToTeeSheet(error.date)}>{t("Go to tee sheet")} <FontAwesomeIcon icon={faArrowRight}/></Button>
                                </div>
                            );
                        })}
                    </div>
                    </>
                ) : validatedTimes?.valid_count > 0 ? (
                    <div className="ui-duplicate-booking-modal-status-list-card">
                        <div className="flex flex-column gap-2">
                            <div>
                                <Badge size="small" type="success"><FontAwesomeIcon icon={faCheck}/> {t("All available")}</Badge>
                             </div>
                            <p className="ui-duplicate-booking-modal-facility-primary" style={{padding: "0 8px"}}>{moment(state?.startDate, "YYYY-MM-DD")?.format("ll")} - {moment(state?.endDate, "YYYY-MM-DD")?.format("ll")} {`(${(state?.occurances?.includes("everyday") ? "" : t("Every "))?.concat(state?.occurances?.map(o => valueToString(o))?.join(", "))})`}</p>
                            <p className="ui-duplicate-booking-modal-facility-secondary" style={{padding: "0 8px"}}>{moment(`${teetime?.start_time}`, "HH:mm")?.format("LT")} • {booking?.quantity} {booking?.quantity === 1 ? t("Player") : t("Players")} • {convertHoles(booking?.holes, booking?.tee_time?.course)} {t("holes")}</p>
                        </div>
                        
                    </div>
                ) : <Callout type="info" title={t("No Tee Times Found")} content={t("Adjust the date and occurrences selections and try again")}/>}
                </div>
            ) : null}
            
        </Sheet>
        </Portal>
    );
}